/**
 * El circuito de una sección entera, para el chip S·F·E·C de la lista de bloque.
 *
 * Responde la pregunta que hasta ahora obligaba a bajar dos pantallas: **por
 * qué esta sección está en rojo**. Un chip que solo dice «Irregular» nombra el
 * resultado; esto enseña el recorrido que llevó a él.
 *
 * **Carga por su cuenta, y solo al abrirse.**
 *
 * La proyección de la lista trae por sección `{ estado, tono, etiqueta }` y
 * nada más, que es lo correcto: meter en ella los documentos con sus
 * requerimientos, sus consultas y sus pasos multiplicaría el tamaño de la
 * respuesta por cada uno de los treinta trabajadores del bloque para enseñar
 * algo que casi nadie mira. Así que el detalle se pide aquí, y solo del
 * expediente sobre el que alguien se posa.
 *
 * Funciona porque Radix **no monta el contenido del panel hasta abrirlo**: este
 * componente no existe —y por tanto su consulta no se dispara— mientras el
 * panel esté cerrado. Al segundo vistazo ya está en la caché de TanStack Query
 * y no hay petición.
 */
import { Skeleton } from '@mpfn/ui'
import { useExpediente } from '../api/consultas.js'
import type { SeccionVerificable } from '../api/tipos.js'
import { Datos } from './Datos.js'
import { SeguimientoDeDocumento } from './SeguimientoDeDocumento.js'

export function SeguimientoDeSeccion({
  expedienteId,
  seccion,
  titulo,
}: {
  expedienteId: string
  seccion: SeccionVerificable
  titulo: string
}) {
  const expediente = useExpediente(expedienteId)

  return (
    <div className="sigfisc-circuito">
      <header className="sigfisc-circuito__cabecera">
        <strong className="sigfisc-circuito__titulo">{titulo}</strong>
      </header>

      <Datos
        estado={expediente}
        // `UI-10`: la silueta tiene la forma de lo que va a llegar —dos
        // documentos con su línea de pasos—, no un girador genérico.
        silueta={
          <div className="sigfisc-silueta">
            <Skeleton className="sigfisc-silueta__titulo" />
            <Skeleton className="sigfisc-silueta__fila" />
            <Skeleton className="sigfisc-silueta__fila" />
          </div>
        }
      >
        {(datos) => {
          const documentos = (
            datos.secciones.find((item) => item.seccion === seccion)?.documentos ?? []
          ).filter((documento) => !documento.descartado)

          if (documentos.length === 0) {
            return (
              <p className="sigfisc-circuito__vacio">
                Sin documentos declarados en esta sección.
              </p>
            )
          }

          return (
            <ul className="sigfisc-circuito__documentos">
              {documentos.map((documento) => (
                <li key={documento.id}>
                  {/* Con cabecera: aquí caben varios documentos y hay que
                      poder distinguirlos. */}
                  <SeguimientoDeDocumento documento={documento} cabecera />
                </li>
              ))}
            </ul>
          )
        }}
      </Datos>
    </div>
  )
}
